import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, FileText, Headphones, Video, Layers, ClipboardCheck, NotebookPen, Download } from "lucide-react";
import api from "@/lib/api";
import ProgressBar from "@/components/ProgressBar";
import FlashCardDeck from "@/components/FlashCardDeck";
import { toast } from "sonner";

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "video", label: "Video", icon: Video },
    { key: "audio", label: "Audio", icon: Headphones },
    { key: "docs", label: "Training Docs", icon: FileText },
    { key: "flashcards", label: "Flash Cards", icon: Layers },
    { key: "quiz", label: "Quiz", icon: ClipboardCheck },
    { key: "notes", label: "Notes", icon: NotebookPen },
];

export default function ModuleDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("overview");
    const [note, setNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const refresh = () => api.get(`/modules/${id}`).then((r) => {
        setData(r.data);
        setNote(r.data?.note?.body ?? "");
    });

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

    const completedSet = useMemo(() => new Set(data?.completed_lesson_ids ?? []), [data]);

    if (!data) return <div className="text-sm text-ink-500" data-testid="module-loading">Loading module…</div>;
    const { module: mod, lessons, flashcards, quiz, questions, quiz_passed: quizPassed } = data;

    const videos = lessons.filter((l) => l.kind === "video");
    const audios = lessons.filter((l) => l.kind === "audio");
    const docs = lessons.filter((l) => l.kind === "document");
    const totalLessons = lessons.length;
    const hasQuiz = !!quiz;
    const totalItems = totalLessons + (hasQuiz ? 1 : 0);
    const doneItems = completedSet.size + (quizPassed ? 1 : 0);
    const progressPct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0;
    const moduleComplete = totalItems > 0 && doneItems === totalItems;

    // Per-module tab visibility. Overview, Video and Audio always render.
    // Every other section is hidden when it has no content for THIS module,
    // so trainees only see tabs that actually contain something.
    // Notes is special: it's user-driven, so we also keep the tab visible
    // while the user is actively on it (so they can save their first note).
    const hasSavedNote = Boolean(data?.note?.body);
    const tabHasContent = {
        overview: true,
        video: true,
        audio: true,
        docs: docs.length > 0,
        flashcards: flashcards.length > 0,
        quiz: hasQuiz && questions.length > 0,
        notes: hasSavedNote || tab === "notes",
    };
    const visibleTabs = TABS.filter((t) => tabHasContent[t.key]);
    // If the current tab was hidden (e.g. user previously viewed it then admin
    // removed the content), bounce back to Overview.
    if (!visibleTabs.find((t) => t.key === tab) && tab !== "overview") {
        setTab("overview");
    }

    const markComplete = async (lessonId) => {
        await api.post("/progress", { lesson_id: lessonId, module_id: mod.id, completed: true });
        toast.success("Marked as complete");
        refresh();
    };

    const saveNote = async () => {
        setSavingNote(true);
        try {
            await api.post("/notes", { module_id: mod.id, body: note });
            toast.success("Notes saved");
        } finally { setSavingNote(false); }
    };

    return (
        <div className="space-y-10" data-testid="module-detail-page">
            <div>
                <Link to="/modules" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900 transition-colors" data-testid="module-back-link">
                    <ArrowLeft size={12} /> Modules
                </Link>
                <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-2xl">
                        <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">{mod.title}</h1>
                        {mod.description && <p className="mt-3 text-base text-ink-500">{mod.description}</p>}
                    </div>
                    <div className="min-w-[220px]">
                        <ProgressBar value={progressPct} label={`${mod.estimated_minutes ?? 30} min · Progress`} testId="module-progress" />
                    </div>
                </div>
                {moduleComplete && (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-mono text-haevn-700 bg-haevn-50 border border-haevn-100 px-3 py-1.5">
                        <Check size={12} /> Module complete
                    </div>
                )}
                {!moduleComplete && hasQuiz && completedSet.size === totalLessons && !quizPassed && (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-mono text-clay-500 bg-clay-50 px-3 py-1.5">
                        Quiz pending — pass at 100% to complete the module
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="border-b border-ink-300 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-thin">
                <div className="flex gap-6 min-w-max">
                    {visibleTabs.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`tab-${t.key}`}
                            className={`relative pb-3 text-sm transition-colors ${tab === t.key ? "text-ink-900" : "text-ink-500 hover:text-ink-900"}`}
                        >
                            {t.label}
                            {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-haevn-700" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab content */}
            <div>
                {tab === "overview" && (
                    <Overview
                        mod={mod}
                        lessons={lessons}
                        flashcards={flashcards}
                        quiz={quiz}
                        questions={questions}
                        progressPct={progressPct}
                        hasSavedNote={hasSavedNote}
                        onAddNote={() => setTab("notes")}
                    />
                )}

                {tab === "video" && (
                    <LessonList items={videos} kind="video" completedSet={completedSet} onComplete={markComplete} />
                )}

                {tab === "audio" && (
                    <LessonList items={audios} kind="audio" completedSet={completedSet} onComplete={markComplete} />
                )}

                {tab === "docs" && (
                    <DocsTab items={docs} completedSet={completedSet} onComplete={markComplete} />
                )}

                {tab === "flashcards" && (
                    flashcards.length === 0 ? (
                        <div className="text-sm text-ink-500" data-testid="module-flashcards-empty">No flash cards in this module yet.</div>
                    ) : (
                        <FlashCardDeck cards={flashcards} />
                    )
                )}

                {tab === "quiz" && (
                    quiz ? (
                        <div className="bg-white border border-ink-300 p-8 max-w-2xl">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono">Quiz</div>
                            <h3 className="font-heading text-2xl mt-2">{quiz.title}</h3>
                            {quiz.description && <p className="text-sm text-ink-500 mt-2">{quiz.description}</p>}
                            <div className="mt-3 text-xs font-mono uppercase tracking-[0.15em] text-ink-400">
                                {questions.length} questions · Pass at {quiz.pass_threshold}%
                            </div>
                            <button
                                onClick={() => navigate(`/quizzes/${quiz.id}`)}
                                disabled={questions.length === 0}
                                data-testid="module-start-quiz-btn"
                                className="mt-6 bg-haevn-700 text-white px-6 py-2.5 rounded-sm hover:bg-haevn-900 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                {questions.length === 0 ? "No questions yet" : "Begin quiz"}
                            </button>
                        </div>
                    ) : (
                        <div className="text-sm text-ink-500" data-testid="module-quiz-empty">No quiz attached to this module.</div>
                    )
                )}

                {tab === "notes" && (
                    <div className="max-w-2xl">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Personal notes</div>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Capture takeaways, scripts, observations…"
                            data-testid="module-notes-textarea"
                            className="w-full min-h-[260px] bg-white border border-ink-300 rounded-sm p-4 text-sm focus:outline-none focus:border-haevn-700"
                        />
                        <button
                            onClick={saveNote}
                            disabled={savingNote}
                            data-testid="module-save-notes-btn"
                            className="mt-4 bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 transition-colors disabled:opacity-50"
                        >
                            {savingNote ? "Saving…" : "Save notes"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Overview({ mod, lessons, flashcards, quiz, questions, progressPct, hasSavedNote, onAddNote }) {
    return (
        <div className="grid lg:grid-cols-3 gap-px bg-ink-300 border border-ink-300">
            <div className="lg:col-span-2 bg-white p-8 md:p-10">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-3">Brief</div>
                <p className="text-lg md:text-xl font-editorial leading-relaxed text-ink-900 max-w-2xl">
                    {mod.brief || mod.description || "An overview will be provided by your trainer."}
                </p>

                <div className="mt-10">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-3">In this module</div>
                    <ul className="space-y-2 text-sm text-ink-700">
                        <li className="flex gap-3"><span className="text-ink-400">·</span> {lessons.filter((l)=>l.kind==="video").length} video lessons</li>
                        <li className="flex gap-3"><span className="text-ink-400">·</span> {lessons.filter((l)=>l.kind==="audio").length} audio lessons</li>
                        {lessons.filter((l)=>l.kind==="document").length > 0 && (
                            <li className="flex gap-3"><span className="text-ink-400">·</span> {lessons.filter((l)=>l.kind==="document").length} training documents</li>
                        )}
                        {flashcards.length > 0 && (
                            <li className="flex gap-3"><span className="text-ink-400">·</span> {flashcards.length} flash cards</li>
                        )}
                        {quiz && questions.length > 0 && (
                            <li className="flex gap-3"><span className="text-ink-400">·</span> {questions.length} quiz questions</li>
                        )}
                    </ul>
                </div>

                {!hasSavedNote && onAddNote && (
                    <div className="mt-10 pt-8 border-t border-ink-200">
                        <button
                            onClick={onAddNote}
                            data-testid="module-add-note-btn"
                            className="text-xs font-mono uppercase tracking-[0.15em] text-haevn-700 hover:text-haevn-900"
                        >
                            + Add a personal note →
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-white p-8">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-3">Status</div>
                <div className="font-heading text-5xl text-ink-900">{progressPct}%</div>
                <div className="text-xs text-ink-500 mt-1">complete</div>
                <div className="mt-6">
                    <ProgressBar value={progressPct} size="sm" />
                </div>
                <div className="mt-8 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-2">Suggested flow</div>
                <ol className="space-y-1.5 text-xs text-ink-700">
                    {(() => {
                        const steps = ["Read the brief"];
                        if (lessons.some((l)=>l.kind==="video")) steps.push("Watch the video");
                        if (lessons.some((l)=>l.kind==="audio")) steps.push("Listen to the audio");
                        if (lessons.some((l)=>l.kind==="document")) steps.push("Read the document");
                        if (flashcards.length > 0) steps.push("Drill the flash cards");
                        if (quiz && questions.length > 0) steps.push("Pass the quiz");
                        return steps.map((label, i) => (
                            <li key={i} className="flex items-baseline gap-2">
                                <span className="text-ink-400 font-mono">{String(i + 1).padStart(2, "0")}</span> {label}
                            </li>
                        ));
                    })()}
                </ol>
            </div>
        </div>
    );
}

function LessonList({ items, kind, completedSet, onComplete }) {
    const [active, setActive] = useState(items[0] ?? null);
    if (items.length === 0) {
        return <div className="text-sm text-ink-500" data-testid={`module-${kind}-empty`}>No {kind} lessons in this module yet.</div>;
    }
    return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="bg-white border border-ink-300 p-6 md:p-8">
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{kind}</div>
                <h3 className="font-heading text-2xl text-ink-900 mt-1">{active?.title}</h3>

                {kind === "video" && active?.content && (
                    <div className="mt-6 aspect-video bg-ink-100 border border-ink-300">
                        <VideoEmbed url={active.content} />
                    </div>
                )}

                {kind === "audio" && active?.content && (
                    <div className="mt-6">
                        <audio controls src={active.content} className="w-full" data-testid="audio-player" />
                    </div>
                )}

                {!active?.content && (
                    <div className="mt-6 border border-dashed border-ink-300 p-10 text-center text-sm text-ink-500">
                        Media URL not provided yet.
                    </div>
                )}

                {active?.transcript && (
                    <details className="mt-6 group">
                        <summary className="cursor-pointer text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900">
                            Transcript
                        </summary>
                        <div className="mt-3 text-sm text-ink-700 leading-relaxed whitespace-pre-line">{active.transcript}</div>
                    </details>
                )}

                <button
                    onClick={() => onComplete(active.id)}
                    disabled={completedSet.has(active.id)}
                    data-testid={`mark-complete-${active.id}`}
                    className="mt-8 inline-flex items-center gap-2 bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 transition-colors disabled:opacity-60"
                >
                    {completedSet.has(active.id) ? <><Check size={14} /> Completed</> : "Mark complete"}
                </button>
            </div>

            <aside className="bg-white border border-ink-300">
                <div className="p-4 border-b border-ink-200 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">
                    All {kind} lessons
                </div>
                <ul className="divide-y divide-ink-200">
                    {items.map((l, i) => (
                        <li key={l.id}>
                            <button
                                onClick={() => setActive(l)}
                                data-testid={`lesson-item-${l.id}`}
                                className={`w-full text-left px-4 py-3 transition-colors ${active?.id === l.id ? "bg-ink-100" : "hover:bg-ink-100/60"}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="font-mono text-xs text-ink-400 mt-0.5">{String(i+1).padStart(2,"0")}</div>
                                    <div className="flex-1">
                                        <div className="text-sm text-ink-900">{l.title}</div>
                                        {l.duration_seconds > 0 && <div className="text-[11px] text-ink-500 mt-0.5">{Math.round(l.duration_seconds/60)} min</div>}
                                    </div>
                                    {completedSet.has(l.id) && <Check size={14} className="text-haevn-700 mt-0.5" />}
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>
        </div>
    );
}

function renderDocBody(content) {
    if (!content) return null;
    const urlRe = /(https?:\/\/[^\s<>"]+)/gi;
    const parts = [];
    let m, last = 0, idx = 0;
    while ((m = urlRe.exec(content)) !== null) {
        if (m.index > last) parts.push({ kind: "text", value: content.slice(last, m.index), key: idx++ });
        parts.push({ kind: "link", value: m[1], key: idx++ });
        last = m.index + m[1].length;
    }
    if (last < content.length) parts.push({ kind: "text", value: content.slice(last), key: idx++ });
    return parts.map((p) => p.kind === "link" ? (
        <a key={p.key} href={p.value} target="_blank" rel="noopener noreferrer"
            className="text-haevn-700 underline underline-offset-2 hover:text-haevn-900 break-all">{p.value}</a>
    ) : (
        <span key={p.key}>{p.value}</span>
    ));
}

function extractPdfUrls(content) {
    if (!content) return [];
    const re = /(https?:\/\/[^\s<>"]+\.pdf[^\s<>"]*)/gi;
    return Array.from(new Set((content.match(re) || [])));
}

function DocsTab({ items, completedSet, onComplete }) {
    const [active, setActive] = useState(items[0] ?? null);
    if (items.length === 0) {
        return <div className="text-sm text-ink-500" data-testid="module-docs-empty">No training documents yet.</div>;
    }
    // Prefer the explicit file_url field; fall back to URL parsing for legacy lessons
    const fileUrl = active?.file_url || extractPdfUrls(active?.content)[0] || null;
    // Body shown to the trainee — strip any inline URLs since the button is the only surface
    const cleanBody = (active?.content || "").replace(/(https?:\/\/[^\s<>"]+)/gi, "").replace(/\n{3,}/g, "\n\n").trim();
    return (
        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
            <article className="bg-white border border-ink-300 p-8 md:p-12 max-w-3xl">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono">Training document</div>
                <h2 className="font-editorial text-3xl text-ink-900 mt-2">{active?.title}</h2>

                {fileUrl && (
                    <div className="mt-6 flex flex-wrap gap-2">
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                            data-testid={`doc-pdf-link-${active.id}`}
                            className="inline-flex items-center gap-2 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900 transition-colors">
                            <FileText size={14} strokeWidth={1.5} /> Open PDF
                        </a>
                        <a href={fileUrl} download
                            data-testid={`doc-pdf-download-${active.id}`}
                            className="inline-flex items-center gap-2 bg-white border border-ink-300 text-ink-900 px-4 py-2 rounded-sm text-sm hover:bg-ink-100 transition-colors">
                            <Download size={14} strokeWidth={1.5} /> Download
                        </a>
                    </div>
                )}

                <div className="mt-8 text-base leading-relaxed text-ink-700 whitespace-pre-line">
                    {cleanBody || "Document content will appear here once added."}
                </div>
                <button
                    onClick={() => onComplete(active.id)}
                    disabled={completedSet.has(active.id)}
                    data-testid={`doc-mark-complete-${active.id}`}
                    className="mt-10 inline-flex items-center gap-2 bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 transition-colors disabled:opacity-60"
                >
                    {completedSet.has(active.id) ? <><Check size={14} /> Completed</> : "Mark complete"}
                </button>
            </article>
            <aside className="bg-white border border-ink-300">
                <div className="p-4 border-b border-ink-200 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">Sections</div>
                <ul className="divide-y divide-ink-200">
                    {items.map((l) => (
                        <li key={l.id}>
                            <button
                                onClick={() => setActive(l)}
                                data-testid={`doc-item-${l.id}`}
                                className={`w-full text-left px-4 py-3 ${active?.id === l.id ? "bg-ink-100" : "hover:bg-ink-100/60"}`}
                            >
                                <div className="text-sm text-ink-900">{l.title}</div>
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>
        </div>
    );
}

function VideoEmbed({ url }) {
    // Convert common youtube/vimeo links to embed
    const yt = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
    if (yt) {
        return <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yt[1]}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="video" data-testid="video-iframe" />;
    }
    const vm = url?.match(/vimeo\.com\/(\d+)/);
    if (vm) return <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${vm[1]}`} allow="autoplay; fullscreen" title="video" data-testid="video-iframe" />;
    return <video controls src={url} className="w-full h-full" data-testid="video-element" />;
}
